import type { DeliveryType, FulfillmentStage, OrderState, OrderView } from '../../../shared/types';

// Re-export shared contracts so consumers import from a single module.
export type { DeliveryType, FulfillmentStage, OrderItemView, OrderView } from '../../../shared/types';

export type ControlTowerBucket = 'ingreso' | 'produccion' | 'despacho' | 'excepcion';

export type DeliveryFilter = 'todas' | DeliveryType;

export type StatusTone = 'amber' | 'blue' | 'green' | 'red' | 'purple';

/**
 * Temporal criticality thresholds (in minutes), measured from order.createdAt.
 * Tune these per stage without touching callers.
 */
export const CRITICALITY_THRESHOLDS = {
  normalMaxMinutes: 10,
  warningMaxMinutes: 20,
} as const;

export type CriticalityLevel = 'normal' | 'warning' | 'critical';

export interface Criticality {
  level: CriticalityLevel;
  minutes: number;
  label: string;
}

/** True when the order is a business/operational exception (cancelled or ghost). */
export function isException(order: OrderView): boolean {
  return (
    order.status === 'cancelado' ||
    order.status === 'fantasma' ||
    order.fulfillmentStage === 'cancelled'
  );
}

/**
 * Visual classification of an order into a Control Tower bucket.
 * Presentation-only: it never replaces the backend status / fulfillmentStage.
 */
export function classifyOrder(order: OrderView): ControlTowerBucket {
  if (isException(order)) return 'excepcion';
  if (order.status === 'pendiente') return 'ingreso';
  if (
    order.status === 'preparacion' ||
    order.fulfillmentStage === 'kitchen' ||
    order.fulfillmentStage === 'delivery_prep'
  ) {
    return 'produccion';
  }
  // ready / transit / handed_off / delivered and any completed order -> despacho/completado
  return 'despacho';
}

export function getCriticality(order: OrderView, now: number): Criticality {
  const elapsedMs = Math.max(0, now - Date.parse(order.createdAt));
  const minutes = Math.floor(elapsedMs / 60_000);
  const level: CriticalityLevel =
    minutes > CRITICALITY_THRESHOLDS.warningMaxMinutes
      ? 'critical'
      : minutes > CRITICALITY_THRESHOLDS.normalMaxMinutes
        ? 'warning'
        : 'normal';
  return { level, minutes, label: `${String(minutes).padStart(2, '0')} min` };
}

export const orderStatusLabels: Record<OrderState, string> = {
  pendiente: 'Pendiente',
  preparacion: 'En preparación',
  completado: 'Completado',
  cancelado: 'Cancelado',
  fantasma: 'Fantasma',
};

export const orderStatusTone: Record<OrderState, StatusTone> = {
  pendiente: 'amber',
  preparacion: 'blue',
  completado: 'green',
  cancelado: 'red',
  fantasma: 'purple',
};

export const fulfillmentLabels: Record<FulfillmentStage, string> = {
  kitchen: 'En cocina',
  delivery_prep: 'En preparación (reparto)',
  ready: 'Listo para entregar',
  transit: 'En reparto',
  delivered: 'Entregado',
  handed_off: 'Retirado / entregado en mostrador',
  cancelled: 'Cancelado',
};

export const deliveryTypeLabels: Record<DeliveryType, string> = {
  delivery: 'Delivery',
  pickup: 'Retiro',
  local: 'En el sitio',
};

export function resolveSelectedOrder(orders: OrderView[], selectedOrder: OrderView | null): OrderView | null {
  if (!selectedOrder) return null;
  return orders.find((order) => order.id === selectedOrder.id) ?? null;
}

/** Compact per-line summary of the order's commercial lines. */
export function summarizeItems(order: OrderView): string {
  return order.items.map((item) => `${item.quantity}× ${item.productName}`).join(', ');
}
